import { useCallback, useLayoutEffect, useRef, useSyncExternalStore } from "react";

// Same-document only. Snapshots contain no credentials or response tuples.
const stores = new Map();
const empty = Object.freeze({});
const storeFor = (orgId) => {
  if (!stores.has(orgId)) stores.set(orgId, { snapshot: empty, listeners: new Set() });
  return stores.get(orgId);
};
const publish = (store, key, entry) => {
  store.snapshot = Object.freeze({ ...store.snapshot, [key]: Object.freeze(entry) });
  store.listeners.forEach((notify) => notify());
};

export default function useAdminAccessMutations(orgId, userId, token) {
  const store = storeFor(orgId);
  const subscribe = useCallback((notify) => {
    store.listeners.add(notify);
    return () => store.listeners.delete(notify);
  }, [store]);
  const getSnapshot = useCallback(() => store.snapshot, [store]);
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const lifetime = useRef(null);
  useLayoutEffect(() => {
    const current = { orgId, userId, token, id: {}, verified: new Map(), reads: new Set() };
    lifetime.current = current;
    return () => { if (lifetime.current === current) lifetime.current = null; };
  }, [orgId, userId, token]);
  const currentLifetime = () => {
    const current = lifetime.current;
    return current && current.orgId === orgId && current.userId === userId && current.token === token ? current : null;
  };
  const needsVerification = (adminId) => {
    const entry = store.snapshot[adminId];
    return entry?.state === "complete" && currentLifetime()?.verified.get(adminId) !== entry;
  };
  return {
    begin(adminId) {
      const current = currentLifetime();
      const entry = store.snapshot[adminId];
      if (!current || !orgId || (entry && (entry.state !== "complete" || needsVerification(adminId)))) return null;
      const operation = { key: adminId, lifetime: current.id };
      publish(store, adminId, { state: "pending", operation });
      return operation;
    },
    isCurrent(operation) {
      return currentLifetime()?.id === operation.lifetime && store.snapshot[operation.key]?.operation === operation;
    },
    hold(operation) {
      if (store.snapshot[operation.key]?.operation === operation) publish(store, operation.key, { state: "unknown", operation });
    },
    finish(operation) {
      const entry = store.snapshot[operation.key];
      if (entry?.operation !== operation || entry.state === "unknown") return;
      const complete = Object.freeze({ state: "complete" });
      if (currentLifetime()?.id === operation.lifetime) currentLifetime().verified.set(operation.key, complete);
      publish(store, operation.key, complete);
    },
    isBlocked(adminId) {
      const entry = store.snapshot[adminId];
      return !!entry && (entry.state !== "complete" || needsVerification(adminId));
    },
    isUnknown(adminId) { return store.snapshot[adminId]?.state === "unknown"; },
    needsVerification,
    async refresh(adminId, read, apply) {
      const current = currentLifetime();
      const entry = store.snapshot[adminId];
      if (!current || !needsVerification(adminId) || current.reads.has(adminId)) return false;
      current.reads.add(adminId);
      const snapshot = store.snapshot;
      try {
        const result = await read();
        if (currentLifetime() !== current || store.snapshot !== snapshot) return false;
        apply(result);
        current.verified.set(adminId, entry);
        // The immutable snapshot identity changes to notify all subscribers, while
        // the unchanged entry identity keeps each consumer's verification local.
        publish(store, adminId, entry);
        return true;
      } catch {
        return false;
      } finally {
        current.reads.delete(adminId);
      }
    },
  };
}
